import React from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { getColor } from '../../theme/colors';
import { CATEGORY_COLORS, STATUS_COLORS } from '../../config/architectureConfig';

// Small pill helper — resolves the colorName via the theme so we never
// hardcode Tailwind class strings in the cards themselves.
function Pill({ colorName, children, mono = false }) {
  const color = getColor(colorName);
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${color.bg} ${color.text} ${mono ? 'font-mono' : ''}`}
    >
      {children}
    </span>
  );
}

export default function ArchitectureCard({ card, onDrill }) {
  const { name, Icon, logoSrc, category, type, status, description, drillTo } = card;
  const tileColorName = CATEGORY_COLORS[category] || 'grey';
  const tileColor = getColor(tileColorName);
  const statusColorName = STATUS_COLORS[status] || 'grey';
  const isDrillable = Boolean(drillTo) && typeof onDrill === 'function';
  const hasDescription = Boolean(description);

  // Webpack returns either a URL string or {default: url} for asset imports
  const logoUrl = logoSrc
    ? (typeof logoSrc === 'object' ? logoSrc.default || logoSrc : logoSrc)
    : null;

  const HeaderTag = isDrillable ? 'button' : 'div';

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
      <HeaderTag
        {...(isDrillable
          ? { onClick: () => onDrill(drillTo), type: 'button' }
          : {})}
        className={`w-full flex items-center justify-between px-4 py-3 transition-colors text-left ${
          isDrillable ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'
        }`}
      >
        {/* Left: icon tile + name + pills */}
        <div className="flex items-center gap-3 min-w-0">
          <div className={`flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0 ${
            logoUrl ? 'bg-white border border-gray-100' : `${tileColor.bg} ${tileColor.text}`
          }`}>
            {logoUrl
              ? <img src={logoUrl} alt={name} className="w-6 h-6 object-contain" />
              : Icon && <Icon size={20} />}
          </div>
          <div className="text-left min-w-0">
            <div className="text-sm font-semibold text-gray-800 truncate">{name}</div>
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              <Pill colorName={tileColorName}>{category}</Pill>
              {type && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-gray-100 text-gray-600">
                  {type}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Right: status pill + chevron when drillable */}
        <div className="flex items-center gap-3 flex-shrink-0 ml-3">
          <Pill colorName={statusColorName}>{status}</Pill>
          {isDrillable && <ChevronRightIcon className="w-4 h-4 text-gray-400" />}
        </div>
      </HeaderTag>

      {hasDescription && (
        <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-3 text-xs text-gray-600">
          {description}
        </div>
      )}
    </div>
  );
}
